import React from "react";
import styled, { withTheme } from "styled-components";
import { Dialog, Button } from "@vitalsource/vstui";
import { cloud } from "@vitalsource/vstui/colors";
import { connect } from "react-redux";
import { actions as modalActions } from "../../redux/modals";
import { Tab, Tabs, TabList, TabPanel } from "../components/tabs";
import { OffScreenStyles } from "../components/mixins";
import { t } from "../core/translate";

const StyledTabList = styled(TabList)`
  & > div {
    width: 33%;
    text-align: center;
  }
  & > div:first-child {
    text-align: left;
  }
  & > div:last-child {
    text-align: right;
  }
  list-style: none;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
`;

const ScrollOffset = styled.div`
  padding-top: ${props => (props.tabsVisible ? "44px" : 0)};
`;

const PanelContainer = styled.div`
  // Dialog Buttons are absolutely positioned
  // Clear to prevent elements crashing
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  .polite-description {
    padding: 0 10px;
  }
`;

const StyledTab = styled(Tab)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 6px 0 6px;
  display: inline-block;
  background: ${cloud.getShade(4)};
  cursor: pointer;
  span {
    ${OffScreenStyles};
  }
  &.react-tabs__tab--selected {
    background: ${props => props.theme.color.primary.getShade(6)};
  }
`;

const OnboardingImage = styled.div`
  height: 100%;
  width: 100%;
  text-align: center;
  img {
    max-height: 100%;
    max-width: 100%;
  }
`;

const OnboardingCard = styled.div`
  padding: 10px;
`;

class OnboardingDialog extends React.Component {
  constructor() {
    super();
    this.state = { index: 0 };
    this.tabs = {};
  }

  componentDidMount() {
    window.addEventListener("keydown", this.keyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.keyDown);
  }

  keyDown = e => {
    e.stopPropagation();
    switch (e.keyCode) {
      case 37:
        this.prevCard(e);
        break;
      case 39:
        this.nextCard(e);
        break;
      default:
        break;
    }
  };

  nextCard = () => {
    const { index } = this.state;
    this.setState(prevState => ({ index: prevState.index + 1 }));
  };

  prevCard = () => {
    if (this.state.index > 0) {
      this.setState(prevState => ({ index: prevState.index - 1 }));
    }
  };

  goToCard = index => {
    this.setState({ index });
  };

  render() {
    const { closeModal, cards, ...rest } = this.props;
    const card = cards[this.state.index];
    let nextFinishButton = "";

    if (this.state.index === cards.length - 1) {
      nextFinishButton = (
        <Button onClick={closeModal}>{t("base.finish", "Finish")}</Button>
      );
    } else {
      nextFinishButton = (
        <Button onClick={this.nextCard}>{t("base.next", "Next")}</Button>
      );
    }

    const TabsContainer = (
      <Tabs
        selectedIndex={Math.max(0, this.state.index)}
        onSelect={index => false}
      >
        <StyledTabList>
          <div>
            <Button onClick={this.prevCard} naked>
              {t("base.previous", "Previous")}
            </Button>
          </div>
          <div>
            {cards.map((card, i) => (
              <StyledTab
                onClick={() => {
                  this.setState({ index: i });
                }}
                key={`card-tab-${i}`}
                innerRef={x => (this.tabs[i] = x)}
              >
                <span>{card.title}</span>
              </StyledTab>
            ))}
          </div>
          <div>{nextFinishButton}</div>
        </StyledTabList>
        <PanelContainer className="panel-container">
          {cards.map((card, i) => (
            <TabPanel key={`card-content-${i}`}>
              <OnboardingCard>
                <OnboardingImage>
                  <img src={card.image} alt={card.title} role="presentation" />
                </OnboardingImage>
              </OnboardingCard>
            </TabPanel>
          ))}
          <p className="polite-description" aria-live="polite">
            {card.description}
          </p>
        </PanelContainer>
      </Tabs>
    );

    const SingleTab = (
      <PanelContainer className="panel-container" key={`card-content`}>
        <OnboardingCard>
          <OnboardingImage>
            <img src={card.image} alt={card.title} role="presentation" />
          </OnboardingImage>
        </OnboardingCard>
        <p className="polite-description" aria-live="polite">
          {card.description}
        </p>
      </PanelContainer>
    );

    return (
      <Dialog
        open
        onRequestClose={closeModal}
        size="large"
        rootSelector={"#bookshelf"}
        title={card.title || "loading"}
        darkHeader={false}
        {...rest}
      >
        {cards.length <= 1 ? SingleTab : TabsContainer}
      </Dialog>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  closeModal: () => dispatch(modalActions.closeModal())
});

export default withTheme(connect(null, mapDispatchToProps)(OnboardingDialog));
